"use client"

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface WorkflowStep {
  title: string;
  description: string;
}

interface WorkflowSchemeProps {
  className?: string;
  title?: string;
  workStepData?: {
    title?: string;
    description?: string;
    items?: WorkflowStep[];
  } | null;
}

export default function WorkflowScheme({ 
  className, 
  title,
  workStepData
}: WorkflowSchemeProps) {
  // Если нет данных, не показываем компонент
  if (!workStepData || !workStepData.items || workStepData.items.length === 0) {
    return null;
  }
  
  const displayTitle = title || workStepData.title || 'Схема работы';
  const steps = workStepData.items.map((step, index) => ({
    number: index + 1,
    title: step.title,
    description: step.description,
  }));
  return (
    <section className={cn('py-6 sm:py-16', className)}>
      <div className="w-full">
        <h2 className="text-xl sm:text-3xl font-semibold text-slate-900 mb-6 sm:mb-12">
          {displayTitle}
        </h2>
        
        {/* Desktop/Tablet Layout */}
        <div className="hidden md:block relative">
          {/* Steps Container */}
          <div className="relative grid grid-cols-4 gap-4 lg:gap-8">
            {/* Connecting Line - positioned at center of 30px badge (15px) and 36px badge (18px) */}
            <div className="absolute top-[15px] left-0 right-0 h-0.5 bg-slate-200 z-0 sm:top-[18px]" />
            
            {steps.map((step, index) => (
              <div key={step.number} className="flex flex-col items-center relative z-10">
                {/* Number Badge Container - centers badge on line with exact height */}
                <div className="mb-6 flex items-center justify-center h-[30px] sm:h-[36px]">
                  <Badge 
                    variant="default" 
                    className="w-[30px] h-[30px] sm:w-[36px] sm:h-[36px] rounded-full flex items-center justify-center p-0 text-white bg-slate-900 border-0 text-sm sm:text-base font-semibold relative z-10"
                  >
                    {step.number}
                  </Badge>
                </div>
                
                {/* Step Content */}
                <Card className="w-full border-slate-200 shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base sm:text-lg font-semibold text-slate-900 leading-tight">
                      {step.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
                      {step.description}
                    </p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="md:hidden space-y-3">
          {steps.map((step) => (
            <div key={step.number} className="flex gap-2">
              {/* Number Badge */}
              <div className="flex-shrink-0">
                <Badge 
                  variant="default" 
                  className="w-[30px] h-[30px] rounded-full flex items-center justify-center p-0 text-white bg-slate-900 border-0 text-sm font-semibold"
                >
                  {step.number}
                </Badge>
              </div>
              
              {/* Step Content */}
              <Card className="flex-1 border-slate-200 shadow-none">
                <CardHeader className="p-4 pb-2">
                  <CardTitle className="text-sm font-semibold text-slate-900 leading-snug">
                    {step.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <p className="text-xs text-slate-700 leading-snug">
                    {step.description}
                  </p>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

